import { NodeId, NodeType } from '@diagram/model/node.model';
import { NodeResizer, ResizeDragEvent, ResizeParams } from '@xyflow/react';
import { memo, useCallback, useRef } from 'react';
import { useDiagramStore } from '../hooks/diagram-store.hook';
import { calculateSide } from '@diagram/utils/geometry';

type Props = {
  nodeId: NodeId;
  type: NodeType;
  show?: boolean;
  minWidth?: number;
  minHeight?: number;
  color?: string;
};

export const NodeResizeControl = memo(
  ({
    nodeId,
    type,
    show = false,
    minWidth = 100,
    minHeight = 10,
    color = '#0C5C65',
  }: Props) => {
    const beginResize = useDiagramStore((s) => s.beginResize);
    const resizeTo = useDiagramStore((s) => s.resizeTo);
    const endResize = useDiagramStore((s) => s.endResize);
    const startOperation = useDiagramStore((s) => s.startOperation);
    const finalizeOperation = useDiagramStore((s) => s.finalizeOperation);
    const raf = useRef<number | null>(null);

    const rect = useCallback(() => {
      const el = document.querySelector(`[data-id="${nodeId}"]`);
      return el?.getBoundingClientRect();
    }, [nodeId]);

    const findSide = useCallback(
      (e: ResizeDragEvent) => {
        const r = rect();
        if (!r) return null;
        return calculateSide(e, r, 5);
      },
      [rect]
    );

    const onResizeStart = useCallback(
      (e: ResizeDragEvent, params: ResizeParams) => {
        if (type === 'shape') {
          startOperation([
            {
              type: 'resize',
              id: nodeId as NodeId,
              status: 'start',
              data: {
                width: params.width,
                height: params.height,
              },
            },
          ]);
          return;
        }
        const side = findSide(e);
        if (!side) return;
        beginResize(nodeId, false, side, params);
      },
      [beginResize, nodeId, findSide, type, startOperation]
    );

    const onResize = useCallback(
      (e: ResizeDragEvent) => {
        if (raf.current) {
          cancelAnimationFrame(raf.current);
        }

        raf.current = requestAnimationFrame(() => {
          resizeTo(e.dx, e.dy, 5);
        });
      },
      [resizeTo]
    );

    const onResizeEnd = useCallback(
      (_: ResizeDragEvent, params: ResizeParams) => {
        if (raf.current) {
          cancelAnimationFrame(raf.current);
          endResize();
          raf.current = null;
        }
        if (type === 'shape') {
          finalizeOperation([
            {
              type: 'resize',
              id: nodeId as NodeId,
              status: 'end',
              data: {
                width: params.width,
                height: params.height,
              },
            },
          ]);
          return;
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [nodeId, type, endResize]
    );

    return (
      <NodeResizer
        minWidth={minWidth}
        minHeight={minHeight}
        isVisible={show}
        autoScale={true}
        lineStyle={{
          borderWidth: type === 'shape' ? '0.1' : 2,
          borderRadius: 0,
          borderColor: color,
        }}
        handleStyle={{
          backgroundColor: color,
        }}
        onResizeStart={onResizeStart}
        onResize={onResize}
        onResizeEnd={onResizeEnd}
      />
    );
  }
);
